import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  DashboardOutlined,
  KeyOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { App as AntApp, Avatar, Button, Dropdown, Layout, Menu, Spin } from 'antd';
import { useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLogout, useMe } from '../api/hooks';
import { PageHeaderProvider, usePageHeaderSlot } from './PageHeader';

const { Header, Sider, Content } = Layout;

const MENU_ITEMS = [
  { key: '/', icon: <DashboardOutlined />, label: <Link to="/">Tổng quan</Link> },
  { key: '/providers', icon: <ApiOutlined />, label: <Link to="/providers">Nhà cung cấp</Link> },
  { key: '/model-groups', icon: <ClusterOutlined />, label: <Link to="/model-groups">Nhóm model</Link> },
  { key: '/workflows', icon: <PartitionOutlined />, label: <Link to="/workflows">Workflow</Link> },
  { key: '/categories', icon: <AppstoreOutlined />, label: <Link to="/categories">Danh mục</Link> },
  { key: '/workers', icon: <CloudServerOutlined />, label: <Link to="/workers">Worker</Link> },
  { key: '/queue', icon: <OrderedListOutlined />, label: <Link to="/queue">Hàng đợi</Link> },
  { key: '/jobs', icon: <UnorderedListOutlined />, label: <Link to="/jobs">Lịch sử</Link> },
  { key: '/webhooks', icon: <ThunderboltOutlined />, label: <Link to="/webhooks">Webhook</Link> },
  { key: '/consumers', icon: <KeyOutlined />, label: <Link to="/consumers">Khóa API</Link> },
  { key: '/logs', icon: <AuditOutlined />, label: <Link to="/logs">Nhật ký</Link> },
  { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">Cài đặt</Link> },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me } = useMe();
  const logout = useLogout();
  const { message } = AntApp.useApp();

  // Khoá menu khớp với route hiện tại (chọn tiền tố dài nhất).
  const selectedKey = useMemo(() => {
    const path = location.pathname;
    const match = MENU_ITEMS.filter(
      (i) => i.key === '/' ? path === '/' : path.startsWith(i.key),
    ).sort((a, b) => b.key.length - a.key.length)[0];
    return match?.key ?? '/';
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      message.success('Đã đăng xuất');
    } catch {
      // ignore
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider breakpoint="lg" collapsedWidth="0" theme="dark" width={232} className="gw-sider">
        <div className="gw-brand">
          <span className="gw-brand__mark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2.5c3.6 4.2 6 7.5 6 10.4A6 6 0 0 1 6 12.9c0-2.9 2.4-6.2 6-10.4Z"
                fill="#fff"
              />
            </svg>
          </span>
          <span className="gw-brand__text">API Gateway</span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={MENU_ITEMS}
        />
      </Sider>
      <PageHeaderProvider>
        <Layout>
          <HeaderBar username={me?.username} onLogout={handleLogout} />
          <Content style={{ margin: 24 }}>
            {logout.isPending ? <Spin /> : <Outlet />}
          </Content>
        </Layout>
      </PageHeaderProvider>
    </Layout>
  );
}

/** Thanh header: bên trái là cụm tiêu đề trang (bơm qua context), bên phải là tài khoản. */
function HeaderBar({ username, onLogout }: { username?: string; onLogout: () => void }) {
  const slot = usePageHeaderSlot();
  return (
    <Header className="gw-header">
      <div className="gw-header__left">{slot}</div>
      <Dropdown
        menu={{
          items: [
            { key: 'logout', icon: <LogoutOutlined />, label: 'Đăng xuất', onClick: onLogout },
          ],
        }}
      >
        <Button type="text" style={{ height: 48 }}>
          <Avatar size="small" style={{ backgroundColor: '#2563eb' }}>
            {(username ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <span style={{ marginLeft: 8 }}>{username ?? '...'}</span>
        </Button>
      </Dropdown>
    </Header>
  );
}
